export const SITE = {
  name: 'Pure Soundscapes',
  url: 'https://puresoundscapes.com',
  title: 'Free Nature Sounds & Ambient Sound Mixer',
  description: 'Mix 17 ambient sounds, including rain, waves, birds, and forest. Adjust each layer, save mixes locally, and explore ten fullscreen nature scenes.',
  image: 'https://puresoundscapes.com/social-preview.png',
  imageAlt: 'Pure Soundscapes: mix nature sounds and explore calming scenes',
};

const routes = {
  '/': { title: SITE.title, description: SITE.description, schemaType: 'WebApplication' },
  '/playlist': { title: SITE.title, description: SITE.description, canonicalPath: '/', schemaType: 'WebApplication' },
  '/gallery': { title: 'Nature Image Gallery with Custom Soundscapes', description: 'Choose from ten nature scenes, combine their ambient sounds, adjust the volume, and enjoy a still or slowly moving fullscreen view.' },
  '/saved': { title: 'Your Saved Sound Mixes', description: 'Revisit the ambient sound combinations saved in this browser, or create a new mix with your own sound levels.', noindex: true },
  '/videos': { title: 'Nature Videos from Our YouTube Channel', description: 'Watch coastal views, ocean waves, and a flight journey from our YouTube channel using the embedded player.' },
  '/blog': { title: 'Nature Sound Guides & Everyday Listening Ideas', description: 'Explore rain, ocean, forest, and campfire sounds with practical guides and casual ideas for reading, working, and everyday breaks.', schemaType: 'CollectionPage' },
  '/pricing': { title: 'Free Nature Sounds & Upcoming Plans', description: 'Use all 17 current ambient sounds, local saved mixes, and ten nature scenes for free. Paid plans are coming soon.' },
  '/billing': { title: 'Billing', description: 'Paid plans are not currently available. View the free listening options.', noindex: true },
  '/404': { title: 'Page Not Found', description: 'This page is not available. Return to the sound mixer or browse our nature sound guides.', noindex: true },
};

export function normalizePath(value = '/') {
  const path = new URL(value, SITE.url).pathname.replace(/\/+/g, '/');
  return path === '/' ? '/' : path.replace(/\/$/, '');
}

export function getMetadata({ url = '/', title, description, image, type, noindex, article } = {}) {
  const path = normalizePath(url);
  const defaults = routes[path] || {};
  const pageTitle = title || defaults.title || SITE.title;
  const canonical = `${SITE.url}${defaults.canonicalPath || path}`;
  return {
    path, canonical, title: pageTitle, fullTitle: `${pageTitle} | ${SITE.name}`,
    description: description || defaults.description || SITE.description,
    image: image ? new URL(image, SITE.url).href : SITE.image,
    type: article ? 'article' : type || 'website',
    noindex: Boolean(noindex || defaults.noindex),
    schemaType: defaults.schemaType || 'WebPage',
  };
}

export function getStructuredData(meta, article) {
  const publisher = { '@type': 'Organization', '@id': `${SITE.url}/#publisher`, name: SITE.name, url: `${SITE.url}/about` };
  const website = { '@type': 'WebSite', '@id': `${SITE.url}/#website`, name: SITE.name, alternateName: 'Nature Touch', url: `${SITE.url}/`, inLanguage: 'en', publisher: { '@id': publisher['@id'] } };
  const page = {
    '@type': meta.schemaType, '@id': `${meta.canonical}#page`, name: meta.title,
    description: meta.description, url: meta.canonical, inLanguage: 'en',
    isPartOf: { '@id': website['@id'] }, publisher: { '@id': publisher['@id'] },
  };
  if (meta.schemaType === 'WebApplication') Object.assign(page, {
    applicationCategory: 'UtilitiesApplication', operatingSystem: 'Web Browser',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
  });
  const graph = [publisher, website, page];
  if (article) {
    const breadcrumbs = [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE.url}/` },
      { '@type': 'ListItem', position: 2, name: 'Listening guides', item: `${SITE.url}/blog` },
      { '@type': 'ListItem', position: 3, name: article.title, item: meta.canonical },
    ];
    page.breadcrumb = { '@id': `${meta.canonical}#breadcrumbs` };
    graph.push({ '@type': 'BreadcrumbList', '@id': `${meta.canonical}#breadcrumbs`, itemListElement: breadcrumbs });
    graph.push({
      '@type': 'BlogPosting', '@id': `${meta.canonical}#article`, headline: article.title,
      description: article.description, url: meta.canonical, inLanguage: 'en',
      mainEntityOfPage: { '@id': page['@id'] }, author: publisher, publisher: { '@id': publisher['@id'] },
      articleSection: article.topic || 'Nature sounds',
      wordCount: article.sections.flatMap(section => section.paragraphs).join(' ').split(/\s+/).length,
    });
  }
  return { '@context': 'https://schema.org', '@graph': graph };
}

export function serializeSchema(schema) {
  return JSON.stringify(schema).replace(/</g, '\\u003c');
}
